import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  Spacer,
  VStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

import { BankAccount } from '__generated__/GQL';
import { getBankAccountDisplayName } from 'common/bankAccount/utils';
import { formatAmount } from 'common/formatAmount';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useErrorHandledMutation from 'common/hooks/useErrorHandledMutation';
import Button from 'components/_core/Button';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import NavigationButton from 'components/NavigationButton';
import useToast from 'components/ToastProvider/useToast';
import routes from 'features/Payments/routes';
import { formatDate } from 'helpers/date';
import * as Layout from 'layouts/StepperLayout';

import {
  CancelScheduledBankTransferDocument,
  CancelScheduledBankTransferMutation,
  CancelScheduledBankTransferMutationVariables,
} from '../hooks/useCancelScheduledBankTransfer/cancelScheduledBankTransfer.gql';
import useScheduledTransferDetails from '../hooks/useScheduledTransferDetails';
import { ScheduledTransferDetailsDocument } from '../hooks/useScheduledTransferDetails/scheduledTransferDetails.gql';
import { MAX_ITEMS_NUMBER } from '../hooks/useScheduledTransfers';
import { ScheduledTransfersDocument } from '../hooks/useScheduledTransfers/scheduledTransfers.gql';
import { getLocaleFromFrequency } from '../utils';
import locales from './locales';

interface ScheduledTransferSidebarParams {
  scheduledTransferId: string;
}

interface ScheduledTransferSidebarProps {
  bankAccounts: Pick<BankAccount, 'bankAccountId' | 'name' | 'type'>[];
}

const ScheduledTransferSidebarContent: FC<ScheduledTransferSidebarProps> = ({
  bankAccounts,
}) => {
  const { scheduledTransferId } = useParams<ScheduledTransferSidebarParams>();
  const { companyProfileId } = useCompanyContext();

  const { showToast } = useToast();

  const { error, loading, refetch, scheduledTransfer } =
    useScheduledTransferDetails(scheduledTransferId);

  const [isCancellingScheduledTransfer, setIsCancellingScheduledTransfer] =
    useState(false);

  const [
    cancelScheduledBankTransfer,
    { loading: cancelScheduledBankTransferLoading },
  ] = useErrorHandledMutation<
    CancelScheduledBankTransferMutation,
    CancelScheduledBankTransferMutationVariables
  >(CancelScheduledBankTransferDocument, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: ScheduledTransfersDocument,
        variables: {
          after: null,
          companyProfileId,
          first: MAX_ITEMS_NUMBER,
        },
      },
      // the mutation doesn't return the cancelled scheduled transfer
      // so we force a refetch of the scheduled transfer details to get the updated status
      {
        query: ScheduledTransferDetailsDocument,
        variables: {
          scheduledBankTransferId: scheduledTransferId,
        },
      },
    ],
  });

  if (loading) {
    return <Loader />;
  }

  if (error || !scheduledTransfer) {
    return (
      <>
        <Flex justifyContent="flex-end" marginBottom="space-32">
          <NavigationButton to={routes.scheduledTransfers} variant="close" />
        </Flex>

        <ErrorCard retry={refetch} />
      </>
    );
  }

  const {
    amount,
    bankAccountId,
    bankTransferRecipient,
    endDate,
    endDateDeterminedBy,
    frequency,
    label,
    nextOccurrenceDate,
    remainingNumberOfOccurrences,
    status,
  } = scheduledTransfer;

  const bankAccount = bankAccounts.find(
    (account) => account.bankAccountId === bankAccountId,
  );

  const isActive = status === 'ACTIVE';

  const onConfirmCancellation = async () => {
    await cancelScheduledBankTransfer({
      variables: {
        input: {
          scheduledBankTransferId: scheduledTransferId,
        },
      },
    }).then(() => {
      showToast({
        message: locales.confirmCancellation.cancelScheduledTransferSuccess,
        type: 'success',
      });
      setIsCancellingScheduledTransfer(false);
    });
  };

  if (isCancellingScheduledTransfer) {
    return (
      <>
        <HStack justifyContent="space-between">
          <NavigationButton
            onClick={() => setIsCancellingScheduledTransfer(false)}
            variant="back"
          />
          <NavigationButton to={routes.scheduledTransfers} variant="close" />
        </HStack>

        <Flex
          animate={{ opacity: 1, y: 0 }}
          as={motion.div}
          flex={1}
          flexDir="column"
          initial={{ opacity: 0, y: -10 }}
        >
          <VStack spacing="space-16">
            <Heading size="lg">{locales.confirmCancellation.title}</Heading>

            <VStack spacing="space-8">
              <Text>{locales.confirmCancellation.nextOccurrencesCancel}</Text>
              <Text>{locales.confirmCancellation.pendingTransfers}</Text>
            </VStack>
          </VStack>
          <Spacer />

          <VStack justify="stretch" paddingTop="space-16" spacing="space-16">
            <Button
              isLoading={cancelScheduledBankTransferLoading}
              onClick={onConfirmCancellation}
              variant="danger"
              width="full"
            >
              {locales.confirmCancellation.confirm}
            </Button>
            <Button
              onClick={() => setIsCancellingScheduledTransfer(false)}
              variant="inline-secondary"
            >
              {locales.confirmCancellation.cancel}
            </Button>
          </VStack>
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex justifyContent="flex-end">
        <NavigationButton to={routes.scheduledTransfers} variant="close" />
      </Flex>

      <Flex
        animate={{ opacity: 1, y: 0 }}
        as={motion.div}
        flex={1}
        flexDir="column"
        initial={{ opacity: 0, y: -10 }}
      >
        <VStack alignItems="stretch" spacing="space-32">
          <VStack align="flex-start" spacing="space-8">
            <Heading size="lg" w="100%">
              {label ||
                locales.formatString(locales.toBeneficiary, {
                  beneficiary: bankTransferRecipient.name,
                })}
            </Heading>
            <SunshineTag colorScheme={isActive ? 'green' : 'red'} size="sm">
              {isActive ? locales.active : locales.cancelled}
            </SunshineTag>
          </VStack>

          <Box>
            <Text fontWeight="weight-500" marginBottom="space-20" size="lg">
              {locales.scheduledTransferDetails}
            </Text>

            <VStack
              align="stretch"
              divider={<Divider />}
              marginBottom="space-32"
              spacing="space-16"
            >
              {bankAccount ? (
                <HStack justifyContent="space-between" spacing="space-24">
                  <Text variant="light">{locales.details.issuer}</Text>
                  <Text variant="primary">
                    {getBankAccountDisplayName(bankAccount)}
                  </Text>
                </HStack>
              ) : null}

              <HStack justifyContent="space-between" spacing="space-24">
                <Text variant="light">{locales.details.recipient}</Text>
                <Text variant="primary">{bankTransferRecipient.name}</Text>
              </HStack>

              <HStack justifyContent="space-between" spacing="space-24">
                <Text variant="light">{locales.details.amount}</Text>
                <Text fontWeight="weight-500" variant="primary">
                  {formatAmount({
                    currency: 'EUR',
                    forcePlusSign: false,
                    value: amount,
                  })}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" spacing="space-24">
                <Text variant="light">{locales.details.frequency}</Text>
                <Text variant="primary">
                  {getLocaleFromFrequency(frequency)}
                </Text>
              </HStack>

              {nextOccurrenceDate ? (
                <HStack justifyContent="space-between" spacing="space-24">
                  <Text variant="light">
                    {locales.details.nextOccurrenceDate}
                  </Text>
                  <Text variant="primary" whiteSpace="nowrap">
                    {formatDate(new Date(nextOccurrenceDate), 'PPP')}
                  </Text>
                </HStack>
              ) : null}

              {endDateDeterminedBy === 'EXPLICIT_DATE' && endDate ? (
                <HStack justifyContent="space-between" spacing="space-24">
                  <Text variant="light">{locales.details.endDate}</Text>
                  <Text variant="primary" whiteSpace="nowrap">
                    {formatDate(new Date(endDate), 'PPP')}
                  </Text>
                </HStack>
              ) : null}

              {endDateDeterminedBy === 'NUMBER_OF_OCCURRENCES' &&
              remainingNumberOfOccurrences ? (
                <HStack justifyContent="space-between" spacing="space-24">
                  <Text variant="light">
                    {locales.details.remainingNumberOfOccurrences}
                  </Text>
                  <Text variant="primary">
                    {locales.formatString(
                      remainingNumberOfOccurrences > 1
                        ? locales.details.occurrences
                        : locales.details.occurrence,
                      { remainingNumberOfOccurrences },
                    )}
                  </Text>
                </HStack>
              ) : null}
            </VStack>
          </Box>
        </VStack>
        <Spacer />

        {isActive ? (
          <VStack justify="stretch" paddingTop="space-16" spacing="space-16">
            <Button
              isDisabled={!isActive}
              onClick={() => setIsCancellingScheduledTransfer(true)}
              variant="danger"
              width="full"
            >
              {locales.cancelScheduledTransfer}
            </Button>
          </VStack>
        ) : null}
      </Flex>
    </>
  );
};

const ScheduledTransferSidebar: FC<ScheduledTransferSidebarProps> = ({
  bankAccounts,
}) => {
  return (
    <Layout.Sidebar
      bg="white"
      borderLeft="1px solid"
      borderLeftColor="border"
      isScrollable
      padding="space-0"
    >
      <VStack
        align="stretch"
        flex={1}
        paddingBottom="space-16"
        paddingTop="space-24"
        paddingX="space-24"
        spacing="space-32"
        width="100%"
      >
        <ScheduledTransferSidebarContent bankAccounts={bankAccounts} />
      </VStack>
    </Layout.Sidebar>
  );
};

export default ScheduledTransferSidebar;
